import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE, type PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { apiGet, apiGetBlob, apiPost, apiPut } from "./client";

export interface ExposureStat {
  label: string;
  value: string;
  sub: string;
  /** the change in percentage points against the previous file date; `comparison` names it ("vs Sep'26") */
  trend: { value: string; direction: "up" | "down" | "flat"; comparison?: string | null };
  /** the range's number on an exposure-range card (what its Download CSV asks for); null on a card that is not a range */
  rangeNo?: number | null;
}

export interface ExposureDetailRow {
  month: string;
  ownedNI: string;
  pctOwned: string;
  extra?: string; // customers in the tier
}

// Exposure Tracking is three small per-card endpoints (stats / trend / detail): the customer-exposure aggregation scales with
// the number of file dates asked for, so each is its own request. None of them takes a ticket size: every figure is the whole book.

export function useExposureTrackingStats() {
  return useQuery({
    queryKey: ["exposure-tracking", "stats"],
    queryFn: () => apiGet<{ stats: ExposureStat[] }>("/exposure-tracking/stats"),
  });
}

/** One line of the Customer Exposure Trend: the key its values sit under in each point, and its legend name. */
export interface ExposureTrendSeries {
  key: string;
  name: string;
}

/** Owned NI in raw dollars at one quarter end: `month` plus one value per series key. */
export type ExposureTrendPoint = { month: string } & Record<string, number | string>;

export function useExposureTrackingTrend() {
  return useQuery({
    queryKey: ["exposure-tracking", "trend"],
    queryFn: () =>
      apiGet<{ series: ExposureTrendSeries[]; trend: ExposureTrendPoint[] }>("/exposure-tracking/trend"),
  });
}

export function useExposureTrackingDetail() {
  return useQuery({
    queryKey: ["exposure-tracking", "detail"],
    queryFn: () =>
      apiGet<{
        /** One table per range with "show trend" on, in the order the ranges are listed
         *  ("$250K–$750K", ">$1M", an office's range too). `key` is the same key as the size's line in the trend chart. */
        cards: Array<{ key: string; label: string; detail: ExposureDetailRow[] }>;
      }>("/exposure-tracking/detail"),
  });
}

// ── Customer exposure ranges: any number of bands, behind the range cards, the trend chart and the trend tables; changed only
//    through an approved proposal ──

/** "any" is every customer of an office, whatever the size (a Franchise card, say); it needs a scope other than "all". */
export type RangeType = "between" | "above" | "any";

/** The scope of every-customer ranges; any other scope is an office's key (franchise_finance ...), from `ExposureRanges.scopes`. */
export const ALL_SCOPE = "all";

export interface ExposureRangeLimits {
  rangeNo: number;
  /** "between" holds customers above `lower` and up to `upper`; "above" holds customers above `lower` (no upper); "any" every
   *  customer of its office */
  type: RangeType;
  lower: number;
  upper: number | null;
  /** "all" (every customer) or an office's scope_key: only that office's rows are looked at */
  scope: string;
  /** the range's card (with Download CSV) is shown at the top of the page */
  showCard: boolean;
  /** the range's table is shown under the Customer Exposure Trend chart, and its line in the chart */
  showTrend: boolean;
  label: string;
}

/** One range in a proposal; `upper` is left out for an "above" range, and `lower` too for an "any" one. A proposal says which ranges
 *  exist, nothing about where they are shown: that is switched directly on an approved range (useSetRangeDisplay). */
export interface RangeDraftItem {
  type: RangeType;
  lower?: number;
  upper?: number;
  scope: string;
}

/** An office a range can be scoped to, read live from the office table: its derived key and its name. */
export interface ExposureScope {
  key: string;
  label: string;
}

export interface ExposureRanges {
  /** the approved ranges, in display order (open-ended first, then bounded, lower amounts first) */
  ranges: ExposureRangeLimits[];
  defaultRanges: ExposureRangeLimits[];
  /** the offices a range can be scoped to (live from the database); empty until the backend's sql/14 is applied (ranges then look at every customer) */
  scopes: ExposureScope[];
  hasPending: boolean;
  /** false where the ranges' tables are not set up yet: the defaults show, and proposing is not possible */
  available: boolean;
  maxRanges: number;
  /** how many range cards can be switched on (shown at the top of the page) at once */
  maxCards: number;
  updatedBy: string | null;
  updatedAt: string | null;
  note: string | null;
}

export interface RangeProposal {
  id: string;
  currentRanges: ExposureRangeLimits[];
  proposedRanges: ExposureRangeLimits[];
  reason: string;
  proposedBy: string | null;
  proposedAt: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export function useExposureRanges() {
  return useQuery({
    queryKey: ["exposure-tracking", "ranges"],
    queryFn: () => apiGet<ExposureRanges>("/exposure-tracking/ranges"),
  });
}

export function useRangeLog(offset: number) {
  return useQuery({
    queryKey: ["exposure-tracking", "range-log", offset],
    queryFn: () =>
      apiGet<PagedResult<RangeProposal>>("/exposure-tracking/range-log", {
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }),
    placeholderData: keepPreviousData,
  });
}

// An approval changes the cards, the chart, the tables and the ranges themselves, so every Exposure Tracking query is invalidated.
export function useProposeRanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: { ranges: RangeDraftItem[]; reason: string }) =>
      apiPost<RangeProposal>("/exposure-tracking/range-proposals", draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exposure-tracking"] }),
  });
}

/** Switches where one approved range is shown (its card, its trend table and line) straight away: no proposal, no approval; the
 *  backend allows contributors and above only. Every Exposure Tracking query is invalidated, since the cards, chart and tables change. */
export function useSetRangeDisplay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { rangeNo: number; showCard: boolean; showTrend: boolean }) =>
      apiPut<ExposureRangeLimits>(`/exposure-tracking/ranges/${args.rangeNo}/display`, {
        showCard: args.showCard,
        showTrend: args.showTrend,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exposure-tracking"] }),
  });
}

/** Puts the approved ranges in a new order straight away: no proposal, no approval (the backend allows contributors and above only).
 *  `order` is every range's number, once each, in the order they should appear. The cards, trend lines and tables follow that order, so
 *  every Exposure Tracking query is invalidated. */
export function useReorderRanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (order: number[]) => apiPut<ExposureRangeLimits[]>("/exposure-tracking/ranges/order", { order }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exposure-tracking"] }),
  });
}

export function useDecideRanges() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; status: "approved" | "rejected"; notes?: string }) =>
      apiPost<RangeProposal>(`/exposure-tracking/range-proposals/${args.id}/decide`, {
        status: args.status,
        notes: args.notes,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["exposure-tracking"] }),
  });
}

/** Downloads the customers in one exposure range (at the latest file date) as a CSV. The backend streams it; the filename is set
 *  here because CORS doesn't expose Content-Disposition cross-origin (same as the APD Monitoring downloads). */
export async function downloadExposureRange(rangeNo: number, filename: string): Promise<void> {
  const blob = await apiGetBlob(`/exposure-tracking/ranges/${rangeNo}/download`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** The backend's own `detail` sentence (e.g. "A proposal is already pending...") when there
 *  is one, rather than axios's generic "Request failed with status code 409". */
export function apiErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return error instanceof Error ? error.message : "Unknown failure.";
}
